import { useEffect, useState } from 'react';
import { Eye, EyeOff, Moon, Sun } from 'lucide-react';
import { LANGUAGE_LABELS, type AppLanguage } from '@/i18n/translations';
import { useI18n } from '@/context/I18nContext';
import { AppLoginBrand } from '@/components/layout/AppBrand';

interface LoginPageProps {
    errorMessage: string;
    isSubmitting: boolean;
    onSubmit: (credentials: { password: string; username: string }) => void;
    onThemeChange: (theme: 'light' | 'dark') => void;
    theme: 'light' | 'dark';
}

function LoginPage({
    errorMessage,
    isSubmitting,
    onSubmit,
    onThemeChange,
    theme,
}: LoginPageProps) {
    const { language, setLanguage, t } = useI18n();
    const [username, setUsername] = useState(__DEFAULT_API_USER__);
    const [password, setPassword] = useState(__DEFAULT_API_PASSWORD__);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    useEffect(() => {
        setUsername(__DEFAULT_API_USER__);
        setPassword(__DEFAULT_API_PASSWORD__);
    }, []);

    return (
        <section className="dashboard-panel w-full max-w-md items-stretch justify-stretch p-0">
            <div className="flex w-full flex-col gap-5 px-6 py-8">
                <div className="flex items-center justify-between gap-3">
                    <label className="toggle border border-base-300 bg-base-200 text-base-content">
                        <input
                            id="login-theme"
                            name="theme"
                            type="checkbox"
                            value="dark"
                            className="theme-controller"
                            checked={theme === 'dark'}
                            onChange={(event) => {
                                onThemeChange(event.target.checked ? 'dark' : 'light');
                            }}
                            aria-label={t('Toggle light and dark theme')}
                        />
                        <Sun className="h-4 w-4" aria-hidden="true" />
                        <Moon className="h-4 w-4" aria-hidden="true" />
                    </label>

                    <label className="form-control w-auto">
                        <span className="sr-only">{t('Language')}</span>
                        <select
                            id="login-language"
                            name="language"
                            className="select select-sm select-ghost w-auto min-w-[9rem]"
                            value={language}
                            onChange={(event) => {
                                setLanguage(event.target.value as AppLanguage);
                            }}
                            aria-label={t('Language')}
                        >
                            {Object.entries(LANGUAGE_LABELS).map(([languageKey, languageLabel]) => (
                                <option key={languageKey} value={languageKey}>
                                    {languageLabel}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className="space-y-2 text-center">
                    <div className="flex justify-center">
                        <AppLoginBrand className="app-login-brand--login" language={language} theme={theme} />
                    </div>
                    <p>{t('Sign in to continue')}</p>
                </div>

                <form
                    className="flex flex-col gap-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSubmit({ password, username });
                    }}
                >
                    <label className="form-control w-full">
                        <span className="label-text mb-2 font-medium">{t('Username')}</span>
                        <input
                            id="login-username"
                            name="username"
                            type="text"
                            className="input input-bordered w-full"
                            value={username}
                            onChange={(event) => {
                                setUsername(event.target.value);
                            }}
                            autoComplete="username"
                        />
                    </label>

                    <label className="form-control w-full">
                        <span className="label-text mb-2 font-medium">{t('Password')}</span>
                        <div className="relative">
                            <input
                                id="login-password"
                                name="password"
                                type={isPasswordVisible ? 'text' : 'password'}
                                className="input input-bordered w-full pr-12"
                                value={password}
                                onChange={(event) => {
                                    setPassword(event.target.value);
                                }}
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm absolute top-1/2 right-1 -translate-y-1/2"
                                onClick={() => {
                                    setIsPasswordVisible((currentValue) => !currentValue);
                                }}
                                aria-label={t(isPasswordVisible ? 'Hide password' : 'Show password')}
                            >
                                {isPasswordVisible
                                    ? <Eye className="h-4 w-4" aria-hidden="true" />
                                    : <EyeOff className="h-4 w-4" aria-hidden="true" />}
                            </button>
                        </div>
                    </label>

                    {errorMessage ? (
                        <div className="alert alert-error text-sm">{errorMessage}</div>
                    ) : null}

                    <button
                        type="submit"
                        className="btn btn-primary mt-2"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? t('Signing in...') : t('Sign in')}
                    </button>
                </form>
            </div>
        </section>
    );
}

export default LoginPage;
